// API client עם הזרקת CSRF טוקן מ-cookie

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`))
  return m ? decodeURIComponent(m[1]) : null
}

type FetchOpts = RequestInit & { json?: unknown }

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, body: unknown, message: string) {
    super(message)
    this.status = status
    this.body = body
  }
}

export async function api<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers = new Headers(opts.headers)
  const method = (opts.method || (opts.json !== undefined ? "POST" : "GET")).toUpperCase()
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json")
  }
  if (method !== "GET" && method !== "HEAD") {
    const csrf = readCookie("csrf")
    if (csrf) headers.set("X-CSRF-Token", csrf)
  }
  const res = await fetch(path, {
    ...opts,
    method,
    headers,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : opts.body,
    credentials: "same-origin",
  })
  const ct = res.headers.get("content-type") || ""
  let body: unknown = null
  if (ct.includes("application/json")) {
    try {
      body = await res.json()
    } catch {
      body = null
    }
  } else if (res.status !== 204) {
    body = await res.text()
  }
  if (!res.ok) {
    const errMsg = typeof body === "object" && body && "error" in body
      ? String((body as { error: unknown }).error)
      : `HTTP ${res.status}`
    throw new ApiError(res.status, body, errMsg)
  }
  return body as T
}

export interface SessionUser {
  email: string
  name: string
  picture: string | null
}

export interface DeviceStatusBattery {
  level?: number
  scale?: number
  percent?: number
  charging?: boolean
  plugged?: string
  temperatureCelsius?: number
  voltageMilliVolts?: number
  health?: string
  technology?: string
  currentNowMicroAmps?: number
  currentAverageMicroAmps?: number
}

export interface DeviceStatusMemory {
  totalRamBytes?: number
  availableRamBytes?: number
  thresholdBytes?: number
  lowMemory?: boolean
  javaMaxBytes?: number
  javaTotalBytes?: number
  javaFreeBytes?: number
}

export interface DeviceStatusStorage {
  internalTotalBytes?: number
  internalAvailableBytes?: number
}

export interface DeviceStatus {
  timestamp?: number
  appVersion?: string
  androidSdk?: number
  androidRelease?: string
  model?: string
  manufacturer?: string
  device?: string
  brand?: string
  battery?: DeviceStatusBattery
  memory?: DeviceStatusMemory
  storage?: DeviceStatusStorage
  powerSaver?: DeviceStatusPowerSaver
  kioskGuard?: DeviceStatusKioskGuard
  /** האם המערכת תפעיל אותנו בעצמה אחרי אתחול */
  isHomeApp?: boolean
  lockTaskState?: "none" | "locked" | "pinned" | "unknown" | string
  uptimeMillis?: number
  elapsedRealtimeMillis?: number
}

export interface Device {
  deviceId: string
  customLabel: string | null
  reportedLabel: string | null
  displayLabel: string
  lastProtocol: string | null
  lastUserAgent: string | null
  lastRemoteAddr: string | null
  lastStatus: DeviceStatus | null
  lastStatusAt: string | null
  lastHelloAt: string | null
  lastConnectedAt: string | null
  lastDisconnectedAt: string | null
  online: boolean
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface DeviceEvent {
  id: number
  deviceId: string
  kind: string
  correlationId: string | null
  name: string | null
  payload: unknown
  ok: boolean | null
  error: string | null
  createdAt: string
}

export interface DeviceStatusHistoryRow {
  id: number
  deviceId: string
  status: DeviceStatus
  batteryPercent: number | null
  batteryCharging: boolean | null
  createdAt: string
}

export type CommandType =
  | "ping"
  | "get_status"
  | "hard_refresh"
  | "reboot"
  | "get_url"
  | "set_url"
  | "get_current_url"
  | "get_domains"
  | "set_domains"
  | "tail_logs"
  | "stream_logs"
  | "set_remote_config"
  | "get_update_config"
  | "set_update_config"
  | "run_update"
  | "install_apk"
  | "get_settings"
  | "set_settings"
  | "restart_app"
  | "power_saver_check"
  | "kiosk_guard_check"
  | "screenshot"
  | "start_mirror"
  | "stop_mirror"
  | "get_mirror_status"
  | "request_projection_consent"
  | "get_ssl_pins"
  | "clear_ssl_pins"
  | "hide_ad"
  | "mirror_keepalive"

export type Screenshot = {
  image: string
  format: string
  width: number
  height: number
  adWasClosed: boolean
  source: "window" | "projection"
  capturedAt: number
}

export type ScreenFrame = {
  image: string
  format: string
  width: number
  height: number
  seq: number
  source: "window" | "projection"
  capturedAt: number
}

export type MirrorStatus = {
  streaming: boolean
  mode: "window" | "projection"
  fps: number
  maxWidth: number
  quality: number
  projectionGranted: boolean
  projectionActive: boolean
  error?: string
}

export async function sendCommand<T = unknown>(
  deviceId: string,
  type: CommandType,
  params?: Record<string, unknown>,
  opts?: { signal?: AbortSignal }
): Promise<T> {
  const res = await api<{ ok: boolean; data: T; error?: string }>(
    `/api/devices/${encodeURIComponent(deviceId)}/command`,
    { json: { type, params }, signal: opts?.signal }
  )
  if (!res.ok) throw new ApiError(502, res, res.error || "command_failed")
  return res.data
}

/* ------------------------------------------------------------- הגדרות מכשיר */

/**
 * הטופס נבנה מהסכמה שהמכשיר עצמו מדווח (get_settings), ולא מרשימה קשיחה כאן -
 * כך מכשיר עם גרסה ישנה פשוט יציג פחות שדות במקום לקבל כתיבות שהוא מתעלם מהן.
 */
export interface SettingField {
  key: string
  type: "bool" | "int" | "string" | "enum"
  group: "security" | "keyboard" | "refresh" | "ads" | "powerSaver"
  label: string
  /** נכנס לתוקף בלי הפעלה מחדש */
  live: boolean
  min?: number
  max?: number
  options?: string[]
}

export type SettingValue = boolean | number | string

export interface DeviceSettings {
  values: Record<string, SettingValue>
  schema: SettingField[]
}

export interface SetSettingsResult {
  applied: string[]
  rejected: string[]
  restartRequired: boolean
  restarting: boolean
  values: Record<string, SettingValue>
}

export interface DeviceStatusPowerSaver {
  enabled?: boolean
  delaySec?: number
  /** המכשיר נמצא *כרגע* במצב חיסכון */
  inSaveMode?: boolean
  onBattery?: boolean
  /** כמה זמן המכשיר מנותק מהחשמל. 0 כשהוא מחובר. */
  unpluggedForMillis?: number
  /** לולאת ה-reconcile רצה במכשיר */
  watchdogRunning?: boolean
}

/**
 * השחזור האוטומטי למצב קיוסק.
 *
 * המכשיר לא מחזיק טיימר אלא מועד יעד מוחלט (dueAtMillis) שנשמר בזיכרון מוגן,
 * ולכן הוא שורד אתחול וכיבוי. remainingMillis שלילי = המועד עבר והשחזור עדיין
 * לא אומת כמוצלח.
 */
export interface DeviceStatusKioskGuard {
  enabled?: boolean
  delayMin?: number
  /** הקיוסק תקין כרגע: device owner, HOME, lock task, והווב־ויו הראשי בחזית */
  healthy?: boolean
  /** מה שבור, כשלא תקין. למשל kiosk-pref-off / not-home-app / foreground-SettingsActivity */
  unhealthyReason?: string | null
  /** למה השחזור מושהה זמנית (שיקוף מסך, התקנה, מצב חיסכון) */
  deferReason?: string | null
  /** ספירה לאחור פעילה */
  armed?: boolean
  dueAtMillis?: number
  /** מילישניות עד השחזור. שלילי = באיחור. 1- = לא דרוך. */
  remainingMillis?: number
  armedReason?: string | null
  /** ניסיונות שחזור רצופים שלא אומתו כמוצלחים */
  failCount?: number
  lastAttemptAtMillis?: number
  lastRestoreAtMillis?: number
  lastRebootAtMillis?: number
  kioskModePref?: boolean
  isDeviceOwner?: boolean
  lockTaskState?: "none" | "locked" | "pinned" | "unknown" | string
  watchdogRunning?: boolean
  foregroundActivity?: string | null
}

/* ------------------------------------------------------------------ עדכונים */

export const ALLOWED_PACKAGES = ["com.abaye.weddinggiftapp", "com.abaye.zboard"] as const

/** חלון העדכון האוטומטי כפי שהמכשיר מדווח עליו (get_update_config). */
export interface UpdateConfig {
  enabled: boolean
  frequency: "daily" | "weekly"
  /** Calendar: 1=ראשון .. 7=שבת */
  dayOfWeek: number
  hour: number
  minute: number
  serverUrl: string
  timeZone: string
  nextRunAt: number | null
  appVersion: string
}

export interface AppBuild {
  id: string
  packageName: string
  appName: string | null
  version: string
  versionCode: number | null
  fileName: string
  sizeBytes: number
  sha256: string
  releaseNotes: string | null
  uploadedBy: string | null
  isCurrent: boolean
  createdAt: string
  downloadUrl: string
}

/** מתג הכיבוי הגלובלי. כשהוא כבוי, אף מכשיר לא ימצא גרסה להוריד. */
export interface UpdatePolicy {
  autoUpdateEnabled: boolean
  updatedAt: string | null
  updatedBy: string | null
}

export function getUpdatePolicy() {
  return api<UpdatePolicy>("/api/updates/policy")
}

export function setUpdatePolicy(autoUpdateEnabled: boolean) {
  return api<UpdatePolicy>("/api/updates/policy", {
    method: "PUT",
    json: { autoUpdateEnabled },
  })
}

export function listBuilds() {
  return api<AppBuild[]>("/api/updates/builds")
}

/** התקדמות העלאה. `total` יכול להיות 0 אם הדפדפן לא יודע את הגודל מראש. */
export interface UploadProgress {
  loaded: number
  total: number
  /** 0..100, או null כשהגודל לא ידוע */
  percent: number | null
}

/**
 * העלאת APK. משתמש ב-XMLHttpRequest ולא ב-fetch: fetch לא חושף התקדמות
 * העלאה (אין upload progress ב-Fetch API), וקובץ APK הוא עשרות מגה.
 * ה-Promise שמוחזר נושא `abort()` לביטול ההעלאה.
 */
export function uploadBuild(
  file: File,
  fields: { packageName: string; version: string; versionCode?: string; releaseNotes?: string },
  onProgress?: (p: UploadProgress) => void,
): Promise<AppBuild> & { abort: () => void } {
  const form = new FormData()
  // השדות לפני הקובץ: השרת קורא את החלקים בזרימה, ורוצה לדעת מה הוא מקבל
  form.append("packageName", fields.packageName)
  form.append("version", fields.version)
  if (fields.versionCode) form.append("versionCode", fields.versionCode)
  if (fields.releaseNotes) form.append("releaseNotes", fields.releaseNotes)
  form.append("file", file)

  const xhr = new XMLHttpRequest()

  const promise = new Promise<AppBuild>((resolve, reject) => {
    xhr.open("POST", "/api/updates/builds")
    xhr.withCredentials = true
    const csrf = readCookie("csrf")
    if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf)

    xhr.upload.onprogress = (e) => {
      onProgress?.({
        loaded: e.loaded,
        total: e.total,
        percent: e.lengthComputable && e.total > 0
          ? Math.round((e.loaded / e.total) * 100)
          : null,
      })
    }

    xhr.onload = () => {
      let body: unknown = null
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        body = xhr.responseText || null
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as AppBuild)
        return
      }
      const errMsg = typeof body === "object" && body && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${xhr.status}`
      reject(new ApiError(xhr.status, body, errMsg))
    }
    xhr.onerror = () => reject(new ApiError(0, null, "network_error"))
    xhr.onabort = () => reject(new ApiError(0, null, "aborted"))
    xhr.ontimeout = () => reject(new ApiError(0, null, "timeout"))

    xhr.send(form)
  })

  return Object.assign(promise, { abort: () => xhr.abort() })
}

export function promoteBuild(id: string) {
  return api<AppBuild>(`/api/updates/builds/${encodeURIComponent(id)}/current`, { method: "POST" })
}

export function deleteBuild(id: string) {
  return api<{ ok: true }>(`/api/updates/builds/${encodeURIComponent(id)}`, { method: "DELETE" })
}

/** דחיפת build ספציפי למכשיר בודד, עכשיו. */
export function installBuild(deviceId: string, buildId: string) {
  return api<{ ok: boolean; data: unknown }>(
    `/api/devices/${encodeURIComponent(deviceId)}/install-build`,
    { json: { buildId } }
  )
}
